// libs
import React, { memo, useState } from "react";

// global components
import ResponsiveLoader from "shared/components/ResponsiveLoader";
import Button from "shared/components/Button";

// components
import InputLabel from "shared/components/AuthModal/components/InputLabel";
import Error from "shared/components/AuthModal/components/Error";
import Title from "shared/components/AuthModal/components/Title";
import Input from "shared/components/AuthModal/components/Input";

// contexts
import { useAuthModalContext } from "shared/components/AuthModal/AuthModalContext";

// helpers
import { validateEmail } from "shared/components/AuthModal/helpers";

// constants
import { TEXTS, FORM_STATE } from "shared/components/AuthModal/constants";
import { BREAKPOINTS } from "shared/styles/base";

// api
import { forgotPassword } from "shared/api/auth";

// styled components
import { Container, Success, Back } from "./styles";

function ForgotPassword() {
    const [error, updateError] = useState("");
    const [success, updateSuccess] = useState("");
    const [email, updateEmail] = useState("");
    const [isEmailError, updateEmailError] = useState(false);
    const [inProgress, updateProgress] = useState(false);
    const { setFormState } = useAuthModalContext();

    const goToLogin = () => setFormState(FORM_STATE.LOGIN);

    const onEmailChange: React.ChangeEventHandler<HTMLInputElement> = e =>
        updateEmail(e.target.value);

    const onFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (inProgress) {
            return;
        }

        updateEmailError(false);

        const { status: emailStatus, error: emailError } = validateEmail(email);
        if (emailStatus === false) {
            // @ts-ignore
            updateError(emailError);
            updateEmailError(true);
            return;
        }

        updateError("");
        updateProgress(true);

        try {
            const response = await forgotPassword(email);

            if (response.error === 1) {
                updateProgress(false);
                updateError(response.message || TEXTS.OOPS);
                return;
            }

            updateSuccess(response.message);
            updateProgress(false);
        } catch (err) {
            updateProgress(false);
            updateError(TEXTS.OOPS);
        }
    };

    return (
        <Container onSubmit={onFormSubmit}>
            <div className="title-container">
                <Back onClick={goToLogin} type="button" aria-label="Back">
                    <i className="icon-arrow" />
                </Back>
                <ResponsiveLoader breakpoint={BREAKPOINTS.MOBILE} up>
                    <Title className="title">{TEXTS.RESET_PASSWORD}</Title>
                </ResponsiveLoader>
            </div>

            <InputLabel>{TEXTS.EMAIL}</InputLabel>

            {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
            <Input
                onChange={onEmailChange}
                data-test="inp-email"
                hasError={isEmailError}
                className="input"
                autoFocus
            />
            {error.length > 0 && <Error data-test="fp-error">{error}</Error>}
            {success.length > 0 && <Success data-test="fp-success">{success}</Success>}
            <Button inProgress={inProgress} disabled={success.length > 0}>
                {TEXTS.RESET_PASSWORD}
            </Button>
        </Container>
    );
}

export default memo(ForgotPassword);
